// src/context.ts
import { effect as effect2 } from "@preact/signals-core";
import { getDocumentElement, getEventTarget } from "@zag-js/dom-query";

// src/signals.ts
import {
  batch as _batch,
  untracked as _untracked,
  computed,
  effect,
  signal
} from "@preact/signals-core";
var MutableSignal = class {
  constructor(value) {
    this.impl = signal(value);
  }
  /**
   * @deprecated
   */
  get value() {
    return this.impl.value;
  }
  /**
   * @deprecated
   */
  set value(value) {
    this.impl.value = value;
  }
  get() {
    return this.impl.value;
  }
  set(value) {
    this.impl.value = value;
  }
  peek() {
    return this.impl.peek();
  }
};
var ComputedSignal = class {
  constructor(fn) {
    this.impl = computed(fn);
  }
  /**
   * @deprecated
   */
  get value() {
    return this.impl.value;
  }
  get() {
    return this.impl.value;
  }
  peek() {
    return this.impl.peek();
  }
};
var batch = _batch;
var untracked = _untracked;
function createSignal(value) {
  return new MutableSignal(value);
}
function createComputed(fn) {
  return new ComputedSignal(fn);
}
function useEffect(element, callback) {
  let cleanup = void 0;
  const dispose = () => {
    cleanup == null ? void 0 : cleanup();
    cleanup = void 0;
  };
  element.addConnectedCallback(() => {
    cleanup == null ? void 0 : cleanup();
    cleanup = effect(callback);
    return dispose;
  });
  return dispose;
}

// src/context.ts
var ContextRequestEvent = class extends Event {
  constructor(key, callback) {
    super("aria-ui/context-request", { bubbles: true, composed: true });
    this.key = key;
    this.callback = callback;
  }
};
var ContextProviderEvent = class extends Event {
  constructor(key) {
    super("aria-ui/context-provider", { bubbles: true, composed: true });
    this.key = key;
  }
};
var ContextImpl = class {
  constructor(key, defaultValue) {
    this.key = key;
    this.defaultValue = defaultValue;
    this.provide = this.provide.bind(this);
    this.consume = this.consume.bind(this);
  }
  provide(element, signal2) {
    const consumers = /* @__PURE__ */ new Map();
    const handleRequest = (event) => {
      const consumer = getEventTarget(event);
      if (
        // Don't consume the event if it's dispatched from the same element.
        element === consumer || event.key !== this.key || consumers.has(event.callback) || !consumer
      ) {
        return;
      }
      event.stopPropagation();
      event.callback(signal2);
      consumers.set(event.callback, consumer);
    };
    const handleProvider = (event) => {
      const provider = getEventTarget(event);
      if (
        // Don't consume the event if it's dispatched from the same element.
        element === provider || event.key !== this.key || !provider
      ) {
        return;
      }
      event.stopPropagation();
      const previousConsumers = Array.from(consumers.entries());
      consumers.clear();
      for (const [callback, consumer] of previousConsumers) {
        consumer.dispatchEvent(new ContextRequestEvent(this.key, callback));
      }
    };
    element.addConnectedCallback(() => {
      ensureAttachRoot(getDocumentElement(element));
      element.addEventListener("aria-ui/context-request", handleRequest);
      element.addEventListener("aria-ui/context-provider", handleProvider);
      element.dispatchEvent(new ContextProviderEvent(this.key));
      return () => {
        element.removeEventListener("aria-ui/context-request", handleRequest);
        element.removeEventListener("aria-ui/context-provider", handleProvider);
      };
    });
  }
  consume(element) {
    const consumer = createSignal(this.defaultValue);
    let dispose;
    let provider;
    let pending;
    element.addConnectedCallback(() => {
      ensureAttachRoot(getDocumentElement(element));
      const onRequest = (contextProvider) => {
        dispose == null ? void 0 : dispose();
        dispose = effect2(() => {
          consumer.set(contextProvider.get());
        });
        provider = contextProvider;
        if (pending !== void 0) {
          contextProvider.set(pending);
          pending = void 0;
        }
      };
      element.dispatchEvent(new ContextRequestEvent(this.key, onRequest));
      return () => {
        dispose == null ? void 0 : dispose();
        dispose = void 0;
        provider = void 0;
      };
    });
    const peek = () => {
      return consumer.peek();
    };
    const get = () => {
      return consumer.get();
    };
    const set = (value) => {
      if (provider) {
        provider.set(value);
      } else {
        pending = value;
      }
    };
    return {
      get,
      /**
       * @deprecated
       */
      get value() {
        return get();
      },
      set,
      /**
       * @deprecated
       */
      set value(value) {
        set(value);
      },
      peek
    };
  }
};
function createContext(key, defaultValue) {
  return new ContextImpl(
    typeof key === "string" ? `aria-ui/context/${key}` : key,
    defaultValue
  );
}
var attachedRoots = /* @__PURE__ */ new WeakSet();
function ensureAttachRoot(root) {
  if (attachedRoots.has(root)) {
    return;
  }
  attachedRoots.add(root);
  attachRoot(root);
}
function attachRoot(element) {
  const store = /* @__PURE__ */ new Map();
  const popPendingRequests = (key) => {
    const pendingRequestData = store.get(key);
    if (pendingRequestData === void 0) {
      return;
    }
    store.delete(key);
    return pendingRequestData;
  };
  const getPendingRequests = (key) => {
    let pendingRequestData = store.get(key);
    if (pendingRequestData === void 0) {
      pendingRequestData = {
        callbacks: /* @__PURE__ */ new WeakMap(),
        requests: []
      };
      store.set(key, pendingRequestData);
    }
    return pendingRequestData;
  };
  const onContextProvider = (event) => {
    const pendingRequestData = popPendingRequests(event.key);
    if (pendingRequestData === void 0) {
      return;
    }
    const { requests } = pendingRequestData;
    for (const { elementRef, callbackRef } of requests) {
      const element2 = elementRef.deref();
      const callback = callbackRef.deref();
      if (element2 === void 0 || callback === void 0) {
      } else {
        element2.dispatchEvent(new ContextRequestEvent(event.key, callback));
      }
    }
  };
  const onContextRequest = (event) => {
    const element2 = getEventTarget(event);
    if (!element2) {
      return;
    }
    const callback = event.callback;
    const pendingRequestData = getPendingRequests(event.key);
    let callbacks = pendingRequestData.callbacks.get(element2);
    if (callbacks === void 0) {
      callbacks = /* @__PURE__ */ new WeakSet();
      pendingRequestData.callbacks.set(element2, callbacks);
    }
    if (callbacks.has(callback)) {
      return;
    }
    callbacks.add(callback);
    pendingRequestData.requests.push({
      elementRef: new WeakRef(element2),
      callbackRef: new WeakRef(callback)
    });
  };
  element.addEventListener("aria-ui/context-request", onContextRequest);
  element.addEventListener("aria-ui/context-provider", onContextProvider);
}

// src/dom.ts
function useEventListener(element, type, listener, options) {
  return useEffect(element, () => {
    element.addEventListener(type, listener, options);
    return () => {
      element.removeEventListener(type, listener, options);
    };
  });
}
function useStyle(element, key, compute) {
  return useEffect(element, () => {
    element.style[key] = compute();
  });
}
function useAttribute(element, key, compute) {
  return useEffect(element, () => {
    const value = compute();
    if (value == null) {
      element.removeAttribute(key);
    } else {
      element.setAttribute(key, String(value));
    }
  });
}
function useAriaAttribute(element, key, compute) {
  return useAttribute(element, key, compute);
}
function useAriaRole(element, role) {
  const compute = typeof role === "string" ? () => role : role;
  return useAttribute(element, "role", compute);
}
function useMutationObserver(element, options) {
  const mutationCounter = createSignal(1);
  useEffect(element, () => {
    const observer = new MutationObserver((mutationList) => {
      if (mutationList.length > 0) {
        mutationCounter.set(mutationCounter.get() + 1);
      }
    });
    observer.observe(element, options);
    return () => {
      observer.disconnect();
    };
  });
  return mutationCounter;
}
function useQuerySelector(element, selector, options = {
  attributeFilter: ["aria-hidden", "aria-selected", "aria-disabled"],
  childList: true,
  subtree: true
}) {
  const mutationCounter = useMutationObserver(element, options);
  return createComputed(() => {
    mutationCounter.get();
    return element.querySelector(selector);
  });
}
function useQuerySelectorAll(element, selector, options = {
  attributeFilter: ["aria-hidden", "aria-selected", "aria-disabled"],
  childList: true,
  subtree: true
}) {
  const mutationCounter = useMutationObserver(element, options);
  return createComputed(() => {
    mutationCounter.get();
    return element.querySelectorAll(selector);
  });
}
function useAnimationFrame(element, effect3) {
  return useEffect(element, () => {
    const callback = effect3();
    if (!callback) {
      return;
    }
    let dispose = void 0;
    const id = requestAnimationFrame(() => {
      dispose == null ? void 0 : dispose();
      dispose = callback() || void 0;
    });
    return () => {
      cancelAnimationFrame(id);
      dispose == null ? void 0 : dispose();
      dispose = void 0;
    };
  });
}

// src/element.ts
import { HTMLElement } from "server-dom-shim";

// src/event.ts
function defineEmit(element, events) {
  return function emit(type, detail) {
    var _a, _b, _c;
    const declaration = events[type];
    if (!declaration) {
      throw new Error(`[aria-ui] Event type "${String(type)}" is not defined`);
    }
    const bubbles = (_a = declaration.bubbles) != null ? _a : false;
    const cancelable = (_b = declaration.cancelable) != null ? _b : true;
    const composed = (_c = declaration.composed) != null ? _c : false;
    const event = new CustomEvent(type, {
      detail,
      bubbles,
      cancelable,
      composed
    });
    element.dispatchEvent(event);
  };
}

// src/prop.ts
import kebabCase from "just-kebab-case";
import mapValues from "just-map-values";
function getFromAttribute(prop) {
  if (prop.fromAttribute) {
    return prop.fromAttribute;
  }
  switch (typeof prop.default) {
    case "string":
      return (value) => {
        if (value == null) {
          return prop.default;
        }
        return String(value);
      };
    case "number":
      return (value) => {
        if (value == null) {
          return prop.default;
        }
        return Number(value);
      };
    case "boolean":
      return (value) => {
        if (value == null) {
          return prop.default;
        }
        return value !== "false";
      };
  }
}
function getToAttribute(prop) {
  if (prop.toAttribute) {
    return prop.toAttribute;
  }
  switch (typeof prop.default) {
    case "string":
    case "number":
    case "boolean":
      return String;
  }
}
function getAttributeMappings(declarations) {
  const attributes = [];
  const attributeToProperty = /* @__PURE__ */ new Map();
  const propertyToAttribute = /* @__PURE__ */ new Map();
  for (const [property, declaration] of Object.entries(declarations)) {
    if (declaration.attribute === false) {
      continue;
    }
    const attribute = typeof declaration.attribute === "string" ? declaration.attribute : kebabCase(property);
    attributes.push(attribute);
    attributeToProperty.set(attribute, property);
    propertyToAttribute.set(property, attribute);
  }
  return [attributes, attributeToProperty, propertyToAttribute];
}
function setupProperties(props) {
  const [observedAttributes, attributeToProperty, propertyToAttribute] = getAttributeMappings(props);
  const fromAttributes = mapValues(props, getFromAttribute);
  const toAttributes = mapValues(props, getToAttribute);
  const attributeChangedCallback = (signals, attribute, newValue) => {
    const property = attributeToProperty.get(attribute);
    if (!property) {
      return;
    }
    const signal2 = signals[property];
    if (!signal2) {
      return;
    }
    const fromAttribute = fromAttributes[property];
    if (!fromAttribute) {
      return;
    }
    signal2.set(fromAttribute(newValue));
  };
  const useProperties = (element, signals) => {
    for (const [property, attribute] of propertyToAttribute.entries()) {
      const signal2 = signals[property];
      if (!signal2) {
        continue;
      }
      const toAttribute = toAttributes[property];
      if (!toAttribute) {
        continue;
      }
      useAttribute(element, attribute, () => {
        var _a;
        const value = signal2.get();
        if (value !== ((_a = props[property]) == null ? void 0 : _a.default)) {
          return toAttribute(value);
        }
        return null;
      });
    }
  };
  return [observedAttributes, attributeChangedCallback, useProperties];
}

// src/types.ts
var getObjectEntries = Object.entries;
var getObjectKeys = Object.keys;

// src/signal-state.ts
function mapValues2(signals) {
  const values = {};
  for (const [key, signal2] of getObjectEntries(signals)) {
    values[key] = signal2.get();
  }
  return values;
}
function mapSignals(values) {
  const signals = {};
  for (const [key, value] of getObjectEntries(values)) {
    signals[key] = createSignal(value);
  }
  return signals;
}
function getStateFromProps(props) {
  const state = {};
  for (const [key, prop] of getObjectEntries(props)) {
    state[key] = createSignal(prop.default);
  }
  return state;
}

// src/element.ts
var BaseElement = class extends HTMLElement {
  constructor() {
    super(...arguments);
    this._connectedCallbacks = [];
    this._disconnectedCallbacks = [];
    this._connected = false;
  }
  /**
   * @hidden
   */
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    for (const callback of this._connectedCallbacks) {
      const dispose = callback();
      if (dispose) {
        this._disconnectedCallbacks.push(dispose);
      }
    }
  }
  /**
   * @hidden
   */
  disconnectedCallback() {
    if (!this._connected) return;
    this._connected = false;
    for (const callback of this._disconnectedCallbacks) {
      callback();
    }
    this._disconnectedCallbacks = [];
  }
  /**
   * @hidden
   */
  addConnectedCallback(callback) {
    this._connectedCallbacks.push(callback);
    if (!this._connected) {
      return;
    }
    const dispose = callback();
    if (dispose) {
      this._disconnectedCallbacks.push(dispose);
    }
  }
};
function defineCustomElement({
  props,
  events,
  setup
}) {
  const [observedAttributes, attributeChangedCallback, useProperties] = setupProperties(props);
  class CustomElement extends BaseElement {
    constructor() {
      super();
      this._s = getStateFromProps(props);
      const emit = defineEmit(this, events);
      setup(this, { state: this._s, emit });
      useProperties(this, this._s);
    }
    attributeChangedCallback(name, oldValue, newValue) {
      attributeChangedCallback(this._s, name, newValue);
    }
  }
  CustomElement.observedAttributes = observedAttributes;
  defineGetterSetter(CustomElement, props);
  return CustomElement;
}
function defineGetterSetter(ElementConstructor, props) {
  for (const prop of Object.keys(props)) {
    Object.defineProperty(ElementConstructor.prototype, prop, {
      get() {
        return this._s[prop].get();
      },
      set(v) {
        ;
        this._s[prop].set(v);
      }
    });
  }
}
function registerCustomElement(name, element) {
  if (typeof customElements === "undefined" || customElements.get(name)) {
    return;
  }
  customElements.define(name, element);
}

// src/props.ts
function assignProps(defaultProps, props) {
  if (!props) {
    return defaultProps;
  }
  const merged = { ...defaultProps };
  for (const key of getObjectKeys(defaultProps)) {
    const prop = props[key];
    if (prop !== void 0) {
      merged[key] = prop;
    }
  }
  return merged;
}
export {
  BaseElement,
  assignProps,
  batch,
  createComputed,
  createContext,
  createSignal,
  defineCustomElement,
  defineEmit,
  getStateFromProps,
  mapSignals,
  mapValues2 as mapValues,
  registerCustomElement,
  untracked,
  useAnimationFrame,
  useAriaAttribute,
  useAriaRole,
  useAttribute,
  useEffect,
  useEventListener,
  useQuerySelector,
  useQuerySelectorAll,
  useStyle
};
